import { OnInit, effect, inject } from '@angular/core';
import { Component } from '@angular/core';
import { LayoutService } from '../../services/app.layout.service';
import { AuthService } from '../../../../core/services/auth.service';
import { UserType } from '../../../enums/user-type.enum';
import { ApiService } from '../../../../core/services/api.service';
import { CompaniesService } from '../../../../features/ads/services/companies.service';
import { DialogService } from 'primeng/dynamicdialog';
import { GeneratePupCreditDialogComponent } from '../../../../features/locations/dialogs/generate-pup-credit-dialog/generate-pup-credit-dialog.component';
import { PupCreditExportService, PupCreditExportRequest } from '../../../../features/locations/services/pup-credit-export.service';
import { TodoStore } from '../../../../store/todo.store';

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html',
    providers: [DialogService],
})
export class AppMenuComponent implements OnInit {

    model: any[] = [];

    private todoStore = inject(TodoStore);
    private myTasksItem: any = null;

    constructor(
        public layoutService: LayoutService,
        private authService: AuthService,
        private apiService: ApiService,
        private companiesSvc: CompaniesService,
        private dialogService: DialogService,
        private pupCreditExport: PupCreditExportService
    ) {
        // Keep the My Tasks badge in sync with open assignments; badge is hidden when 0.
        effect(() => {
            const open = this.todoStore.myTodos().filter((t) => !t.done).length;
            if (this.myTasksItem) {
                this.myTasksItem.badge = open > 0 ? String(open) : undefined;
            }
        });
    }

    ngOnInit() {
        this.buildModel();
    }

    private buildMyTasksItem() {
        const open = this.todoStore.myTodos().filter((t) => !t.done).length;
        this.myTasksItem = {
            label: 'NAV.MY_TASKS',
            icon: 'pi pi-check-square',
            routerLink: ['/dashboard/support/my-todos'],
            badge: open > 0 ? String(open) : undefined,
        };
        this.todoStore.load();
        return this.myTasksItem;
    }

    /**
     * Keys & Lockers is the first DASHBOARD item for everyone; it replaced the legacy
     * "Locker Reservations" entry, so there is no separate "Keys" group.
     */
    private buildModel() {
        const dashboardLanding = { label: 'NAV.KEYS_LOCKERS', icon: 'pi pi-fw pi-key', routerLink: ['/dashboard/keys'] };

        this.model = [
            {
                label: 'NAV.DASHBOARD',
                items: [
                    dashboardLanding,
                    // Add skipLocationChange and set value to True to avoid changing the url
                    // If your use case, need to change then remove the skipLocationChange
                    { label: 'NAV.RESERVE_LOCKER', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/dashboard/reserve']},
                    { label: 'NAV.PAYMENT_METHOD', icon: 'pi pi-fw pi-credit-card', skipLocationChange: true, command: () => this.handleClickPaymentMethod(this.apiService) },
                    { label: 'NAV.PAYMENTS', icon: 'pi pi-fw pi-receipt', routerLink: ['/dashboard/invoices'] }
                ]
            },
        ];

        if (this.authService.hasPermission(UserType.Support)) {
            this.model.push(
                {
                    label: 'NAV.SUPPORT',
                    items: [
                        this.buildMyTasksItem(),
                        {
                            label: 'NAV.LOCATIONS',
                            icon: 'pi pi-map',
                            items: [
                                {label: 'NAV.ALL', icon: 'pi pi-list', routerLink: ['/dashboard/support/locations/all']},
                                {label: 'NAV.SELF_SERVICE_LOCKER', icon: 'pi pi-map-marker', routerLink: ['/dashboard/support/locations/self-service-locker']},
                                {label: 'NAV.GENERATE_PUP_CREDIT', icon: 'pi pi-file-export', command: () => this.openGeneratePupCredit()}
                            ]
                        },
                        { label: 'NAV.USERS', icon: 'pi pi-users', routerLink: ['/dashboard/support/users']},
                        { label: 'NAV.RESERVE_LOCKER', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/dashboard/support/reserve-locker']},
                    ]
                },
                {
                    label: 'NAV.SALES',
                    items: [
                        { label: 'NAV.LEADS', icon: 'pi pi-briefcase', routerLink: ['/dashboard/support/leads']},
                    ]
                },
                {
                    label: 'NAV.ADS',
                    items: [
                        { label: 'NAV.COMPANIES', icon: 'pi pi-building', routerLink: ['/dashboard/support/ads/companies'] },
                        { label: 'NAV.CAMPAIGNS', icon: 'pi pi-megaphone', routerLink: ['/dashboard/support/ads/campaigns'] }
                    ]
                }
            )
        }

        if (this.authService.hasPermission(UserType.Pup)) {
            this.model.push(
                {
                    label: 'NAV.PUP',
                    items: [
                        { label: 'NAV.DASHBOARD', icon: 'pi pi-fw pi-home', routerLink: ['/dashboard/pup/home'], skipLocationChange: true},
                    ]
                }
            )
        }

        // include logout
        this.model.push({
            label: 'NAV.SETTINGS',
            items: [
                { label: 'NAV.LOGOUT', icon: 'pi pi-fw pi-sign-out', command: () => {this.authService.logout(); }}
            ]
        })

        // Conditionally add owner-scoped Ads nav if the current user owns at least one company
        this.companiesSvc.list('mine').subscribe({
            next: (mine) => {
                if (mine && mine.length > 0) {
                    // Insert "My Ads" before the Settings group (which is the last pushed item)
                    const settingsIdx = this.model.length - 1;
                    this.model.splice(settingsIdx, 0, {
                        label: 'NAV.MY_ADS',
                        items: [
                            { label: 'NAV.CAMPAIGNS', icon: 'pi pi-megaphone', routerLink: ['/dashboard/ads/campaigns'] }
                        ]
                    });
                }
            },
            error: () => { /* no-op: do not expose ads nav on failure */ }
        });
    }

    openGeneratePupCredit() {
        const ref = this.dialogService.open(GeneratePupCreditDialogComponent, {
            header: 'Generate PUP Credit',
            width: '32rem',
            breakpoints: { '575px': '90vw' },
            position: 'top',
            dismissableMask: true,
        });

        // The dialog returns a request only when the user submits; closing it any
        // other way (X, mask, Close) returns nothing. Run the export after exit so
        // its sticky toast appears once the modal is gone.
        ref.onClose.subscribe((request: PupCreditExportRequest | undefined) => {
            if (request) {
                this.pupCreditExport.run(request);
            }
        });
    }

    handleClickPaymentMethod(apiService: ApiService) {
        apiService.post('billing-portal-session', { }).subscribe(({
            next: (response: any) => {
                window.open((response as any).url, "_blank");
            }
        }))
    }
}
